import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../utils/api';
import { toast } from './ui/sonner';
import {
  Activity, X, RefreshCw, ArrowRight, Inbox, Clock, ShieldCheck,
  Mail, Phone, Loader2, AlertCircle, ChevronDown, ChevronUp,
  Lock, Unlock, AlertTriangle, Wifi,
} from 'lucide-react';

const ACTION_LABELS = {
  login_success: 'Logged in',
  login_failed: 'Login failed',
  user_register: 'Account registered',
  user_register_failed: 'Registration failed',
  password_reset: 'Password reset',
  password_confirm_failed: 'Password confirm failed',
  admin_reset_password: 'Password reset (by admin)',
  manager_reset_password: 'Password reset (by manager)',
  admin_create_user: 'Account created by admin',
  employer_create_worker: 'Account created by employer',
  agent_create_worker: 'Account created by agent',
  admin_change_role: 'Role changed',
  admin_archive_user: 'User archived',
  admin_delete_user: 'User deleted',
  admin_bulk_archive_users: 'Bulk archive (users)',
  admin_bulk_delete_users: 'Bulk delete (users)',
  job_create: 'Posted a job',
  job_archive: 'Archived a job',
  job_restore: 'Restored a job',
  job_delete: 'Deleted a job',
  job_bulk_archive: 'Bulk archived jobs',
  job_bulk_delete: 'Bulk deleted jobs',
  job_bulk_restore: 'Bulk restored jobs',
};

const ACTION_TONE = {
  login_success: 'emerald',
  login_failed: 'red',
  user_register: 'blue',
  password_reset: 'violet',
  admin_reset_password: 'violet',
  manager_reset_password: 'violet',
  admin_create_user: 'blue',
  employer_create_worker: 'blue',
  agent_create_worker: 'blue',
  admin_change_role: 'rose',
  admin_archive_user: 'amber',
  admin_delete_user: 'red',
  admin_bulk_archive_users: 'amber',
  admin_bulk_delete_users: 'red',
  job_create: 'cyan',
  job_archive: 'amber',
  job_restore: 'emerald',
  job_delete: 'red',
  job_bulk_archive: 'amber',
  job_bulk_delete: 'red',
  job_bulk_restore: 'emerald',
  password_confirm_failed: 'red',
};

const TONE_CLASS = {
  emerald: 'bg-emerald-500',
  red: 'bg-red-500',
  blue: 'bg-blue-500',
  violet: 'bg-violet-500',
  rose: 'bg-rose-500',
  amber: 'bg-amber-500',
  cyan: 'bg-cyan-500',
  slate: 'bg-slate-400',
};

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = Date.now();
  const diff = Math.max(0, now - d.getTime());
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatAbs = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const describeEvent = (e) => {
  const ts = e.target_summary || {};
  if (e.action === 'admin_change_role') {
    return `${ts.old_role || '?'} → ${ts.new_role || '?'}${ts.email ? ` (${ts.email})` : ''}`;
  }
  if (e.action?.startsWith('job_')) {
    return ts.title || ts.email || `target ${e.target_id || ''}`;
  }
  if (e.action === 'admin_change_role') {
    return `${ts.old_role} → ${ts.new_role}`;
  }
  return ts.title || ts.email || ts.full_name || '';
};

/**
 * UserActivityDrawer — slide-in drawer showing a user's recent audit events.
 * Props:
 *  - userId: required
 *  - onClose(): close handler
 */
export default function UserActivityDrawer({ userId, onClose, currentUser }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(30);
  const [expanded, setExpanded] = useState({});
  const [unlockBusy, setUnlockBusy] = useState(false);
  const [unlockReason, setUnlockReason] = useState('False alarm — reviewed and verified');
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockError, setUnlockError] = useState('');

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const res = await apiCall(`/api/users/${userId}/activity?limit=${limit}`);
      setData(res);
    } catch (e) {
      setError(e?.message || 'Failed to load activity');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId, limit]);

  useEffect(() => { load(); }, [load]);

  const submitUnlock = async () => {
    if (!userId) return;
    setUnlockBusy(true);
    setUnlockError('');
    try {
      await apiCall(`/api/admin/users/${userId}/unlock`, {
        method: 'POST',
        body: JSON.stringify({ reason: unlockReason.trim() || 'False alarm — reviewed and verified' }),
      });
      toast.success('Account unlocked and restored');
      setUnlockOpen(false);
      // Reload activity → lockout_info will go away
      await load();
    } catch (e) {
      setUnlockError(e?.message || 'Unlock failed');
    } finally {
      setUnlockBusy(false);
    }
  };

  // Close on ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const u = data?.user;
  const events = data?.events || [];

  return (
    <div
      className="fixed inset-0 z-[70] flex justify-end bg-black/40 backdrop-blur-[2px]"
      onClick={onClose}
      data-testid="user-activity-drawer"
    >
      <aside
        className="bg-white w-full max-w-[480px] h-full shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-start gap-3">
          <div className="p-2 bg-violet-100 rounded-lg flex-shrink-0">
            <Activity className="w-5 h-5 text-violet-700" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-slate-900 truncate">
              {u?.full_name || u?.email || 'User'}
            </h3>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap text-xs text-slate-500">
              {u?.email && (
                <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</span>
              )}
              {u?.phone && (
                <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{u.phone}</span>
              )}
              {u?.role && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 font-semibold">
                  {u.is_super_admin ? 'Super Admin' : u.role}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={load}
            className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg transition"
            title="Refresh"
            data-testid="user-activity-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-lg transition"
            aria-label="Close"
            data-testid="user-activity-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Stats strip */}
        {data && (
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 grid grid-cols-3 gap-2 text-center text-xs">
            <Stat label="Events" value={data.count} />
            <Stat label="Top action" value={ACTION_LABELS[Object.keys(data.by_action || {})[0]] || Object.keys(data.by_action || {})[0] || '—'} />
            <Stat label="First" value={data.first_event ? formatWhen(data.first_event) : '—'} />
          </div>
        )}

        {/* Action filter chips */}
        {data && Object.keys(data.by_action || {}).length > 1 && (
          <div className="px-5 py-2 border-b border-slate-200 flex flex-wrap gap-1.5">
            {Object.entries(data.by_action).slice(0, 8).map(([k, v]) => (
              <span
                key={k}
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                  TONE_CLASS[ACTION_TONE[k] || 'slate']
                } bg-opacity-10 border-current text-slate-700`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${TONE_CLASS[ACTION_TONE[k] || 'slate']}`} />
                {ACTION_LABELS[k] || k} · {v}
              </span>
            ))}
          </div>
        )}

        {/* AUTO-LOCKOUT BANNER + one-click restore */}
        {data?.lockout_info && (
          <div className="mx-5 mt-3 rounded-xl bg-gradient-to-br from-red-50 to-rose-50 border border-red-200 p-4" data-testid="lockout-banner">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                <Lock className="w-5 h-5 text-red-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="text-sm font-bold text-red-900">Account auto-locked</h4>
                  <span className="text-[10px] font-semibold text-red-700 bg-red-100 border border-red-200 rounded-full px-2 py-0.5">
                    {data.lockout_info.fails_24h} fails / 24h · {data.lockout_info.unique_ips} unique IP{data.lockout_info.unique_ips !== 1 ? 's' : ''}
                  </span>
                </div>
                <p className="text-xs text-red-700 mt-1">
                  {data.lockout_info.reason || 'Triggered by suspicious activity'}.
                  {data.lockout_info.locked_at && (
                    <> Locked <strong>{formatWhen(data.lockout_info.locked_at)}</strong>.</>
                  )}
                </p>

                {data.lockout_info.top_ips?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-red-700 flex items-center gap-1">
                      <Wifi className="w-3 h-3" /> Source IPs (last 24h)
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {data.lockout_info.top_ips.map((row) => (
                        <span
                          key={row.ip}
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-red-200 rounded-full text-[11px] font-mono text-red-900"
                          title={`${row.count} failed attempts from ${row.ip}`}
                        >
                          <AlertTriangle className="w-2.5 h-2.5 text-red-500" />
                          {row.ip}
                          <span className="text-red-500 font-sans font-bold">×{row.count}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {!unlockOpen ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      onClick={() => { setUnlockError(''); setUnlockOpen(true); }}
                      className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 hover:bg-emerald-700 transition"
                      data-testid="unlock-restore-btn"
                    >
                      <Unlock className="w-3.5 h-3.5" /> Unlock & Restore
                    </button>
                    <span className="text-[10px] text-red-700 self-center">
                      Super admin can restore this account in one click.
                    </span>
                  </div>
                ) : (
                  <div className="mt-3 p-3 bg-white border border-red-200 rounded-lg space-y-2">
                    <label className="block text-[10px] uppercase tracking-wider font-semibold text-slate-600">
                      Reason (audited)
                    </label>
                    <input
                      type="text"
                      value={unlockReason}
                      onChange={(e) => setUnlockReason(e.target.value)}
                      className="w-full px-2.5 py-2 border border-slate-300 rounded-md text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      data-testid="unlock-reason"
                    />
                    {unlockError && (
                      <div className="text-xs text-red-700">{unlockError}</div>
                    )}
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => setUnlockOpen(false)}
                        disabled={unlockBusy}
                        className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-md transition disabled:opacity-50"
                        data-testid="unlock-cancel"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={submitUnlock}
                        disabled={unlockBusy}
                        className="px-3 py-1.5 bg-emerald-600 text-white rounded-md text-xs font-semibold flex items-center gap-1.5 hover:bg-emerald-700 transition disabled:opacity-50"
                        data-testid="unlock-confirm"
                      >
                        {unlockBusy && <Loader2 className="w-3 h-3 animate-spin" />}
                        Confirm restore
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && !data && (
            <div className="flex items-center justify-center text-slate-500 gap-2 text-sm py-10">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading activity…
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
          {!loading && !error && events.length === 0 && (
            <div className="text-center text-sm text-slate-500 py-12">
              <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              No recorded activity for this user yet.
            </div>
          )}

          <ol className="relative border-l-2 border-slate-200 pl-5 space-y-3">
            {events.map((e) => {
              const tone = ACTION_TONE[e.action] || 'slate';
              const dot = TONE_CLASS[tone] || 'bg-slate-400';
              const isExpanded = !!expanded[e._id];
              return (
                <li key={e._id} className="relative" data-testid={`activity-event-${e._id}`}>
                  <span
                    className={`absolute -left-[27px] top-1 w-3 h-3 rounded-full ${dot} ring-4 ring-white`}
                  />
                  <div className="bg-white border border-slate-200 rounded-xl p-3 hover:border-slate-300 transition">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                          {ACTION_LABELS[e.action] || e.action}
                          {e.direction === 'on_user' && (
                            <span className="text-[10px] font-normal text-rose-600 bg-rose-50 border border-rose-200 rounded px-1 py-0.5">
                              done to user
                            </span>
                          )}
                        </div>
                        {describeEvent(e) && (
                          <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-1 truncate">
                            <ArrowRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
                            <span className="truncate">{describeEvent(e)}</span>
                          </div>
                        )}
                        <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatWhen(e.created_at)} · {formatAbs(e.created_at)}
                        </div>
                      </div>
                      {(e.target_summary || e.metadata) && (
                        <button
                          onClick={() => setExpanded((p) => ({ ...p, [e._id]: !p[e._id] }))}
                          className="text-slate-400 hover:text-slate-700 p-1 rounded transition"
                          data-testid={`activity-toggle-${e._id}`}
                        >
                          {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </button>
                      )}
                    </div>
                    {isExpanded && (
                      <div className="mt-2 pt-2 border-t border-slate-100 space-y-1.5 text-xs">
                        {e.actor_email && e.direction !== 'by_user' && (
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400 w-16 flex-shrink-0">Actor</span>
                            <span className="text-slate-700 truncate">{e.actor_name || e.actor_email}</span>
                          </div>
                        )}
                        {e.ip_address && (
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400 w-16 flex-shrink-0">IP</span>
                            <span className="text-slate-700 font-mono text-[11px]">{e.ip_address}</span>
                          </div>
                        )}
                        {e.target_summary && Object.keys(e.target_summary).length > 0 && (
                          <div>
                            <span className="text-slate-400 text-[10px] uppercase tracking-wider">Target</span>
                            <pre className="mt-1 bg-slate-50 rounded p-2 text-[11px] text-slate-700 whitespace-pre-wrap break-words overflow-hidden">
                              {JSON.stringify(e.target_summary, null, 2)}
                            </pre>
                          </div>
                        )}
                        {e.metadata && Object.keys(e.metadata).length > 0 && (
                          <div>
                            <span className="text-slate-400 text-[10px] uppercase tracking-wider">Metadata</span>
                            <pre className="mt-1 bg-slate-50 rounded p-2 text-[11px] text-slate-700 whitespace-pre-wrap break-words overflow-hidden">
                              {JSON.stringify(e.metadata, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>

          {events.length >= limit && (
            <button
              onClick={() => setLimit(limit + 30)}
              disabled={loading}
              className="mt-4 w-full px-3 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-200 transition disabled:opacity-50"
              data-testid="user-activity-load-more"
            >
              {loading ? 'Loading…' : `Load more (showing ${events.length})`}
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="text-sm font-bold text-slate-900 truncate">{value}</div>
    </div>
  );
}
